import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api from "@/lib/api";
import { fmtMYR, fmtDate } from "@/lib/format";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { History, Loader2, ArrowUp, ArrowDown, Minus } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const PRODUCTS = ["RON95", "DIESEL", "EURO5"];
const sourceBadge = {
  "web-sync": "bg-amber-500/15 text-amber-400",
  manual: "bg-blue-500/15 text-blue-400",
  created: "bg-emerald-500/15 text-emerald-400",
  initial: "bg-white/10 text-[#9CA3AF]",
};

export default function PriceHistory() {
  const { stationId: station, setStationId: setStation, stations } = useStation();
  const [product, setProduct] = useState("all");
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = [];
    if (station !== "all") params.push(`station_id=${station}`);
    if (product !== "all") params.push(`product=${product}`);
    api.get(`/price-history${params.length ? "?" + params.join("&") : ""}`)
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [station, product]);

  const chartData = [...rows].reverse().map((r) => ({
    date: r.changed_at?.slice(0, 10),
    retail: r.retail_price,
    cost: r.purchase_price,
    margin: r.margin,
  }));
  const showChart = product !== "all" && chartData.length > 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Pricing</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Price History</h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Retail &amp; purchase price move together every Wednesday — your <b className="text-white">margin stays fixed</b> (purchase = pump price − margin).</p>
        </div>
        <div className="flex gap-3">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Station</Label>
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger data-testid="ph-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-48"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="all">All Stations</SelectItem>
                {stations.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Product</Label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger data-testid="ph-product-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="all">All Products</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <>
          {showChart && (
            <div className={`${card} p-5 sm:p-6`} data-testid="price-trend-chart">
              <h3 className="font-[Outfit] font-semibold text-lg mb-4">{product} — Retail vs Cost & Margin</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ left: -10, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#9CA3AF", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} domain={["auto", "auto"]} />
                  <Tooltip formatter={(v) => fmtMYR(v)} contentStyle={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#9CA3AF" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="stepAfter" dataKey="retail" name="Retail /L" stroke="#10B981" strokeWidth={2.5} dot />
                  <Line type="stepAfter" dataKey="cost" name="Cost /L" stroke="#3B82F6" strokeWidth={2} dot />
                  <Line type="stepAfter" dataKey="margin" name="Margin /L" stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className={`${card} p-5 sm:p-6`}>
            <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-emerald-400" /><h3 className="font-[Outfit] font-semibold text-lg">Change Log</h3></div>
            {rows.length === 0 ? (
              <div className="text-sm text-[#9CA3AF] py-8 text-center">No price changes recorded yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="price-history-table">
                  <thead>
                    <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                      {["date", "station", "product", "pump", "retail /L", "cost /L", "margin /L", "source", "by"].map((c) => <th key={c} className="py-2 pr-4 font-medium">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const prev = rows[i + 1] && rows[i + 1].pump_id === r.pump_id ? rows[i + 1] : null;
                      const diff = prev ? r.margin - prev.margin : 0;
                      return (
                        <tr key={r.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]" data-testid={`ph-row-${i}`}>
                          <td className="py-2.5 pr-4 metric-value text-white/80">{fmtDate(r.changed_at?.slice(0, 10))}</td>
                          <td className="py-2.5 pr-4">{r.station_name || stations.find((s) => s.id === r.station_id)?.name || "—"}</td>
                          <td className="py-2.5 pr-4">{r.product}</td>
                          <td className="py-2.5 pr-4 text-[#9CA3AF]">{r.label}</td>
                          <td className="py-2.5 pr-4 metric-value text-white">{fmtMYR(r.retail_price)}</td>
                          <td className="py-2.5 pr-4 metric-value text-blue-400">{fmtMYR(r.purchase_price)}</td>
                          <td className="py-2.5 pr-4 metric-value">
                            <span className="inline-flex items-center gap-1 text-amber-400">
                              {fmtMYR(r.margin)}
                              {diff > 0.0001 ? <ArrowUp className="w-3 h-3 text-emerald-400" /> : diff < -0.0001 ? <ArrowDown className="w-3 h-3 text-red-400" /> : <Minus className="w-3 h-3 text-[#374151]" />}
                            </span>
                          </td>
                          <td className="py-2.5 pr-4"><span className={`text-[10px] font-mono px-2 py-0.5 rounded uppercase ${sourceBadge[r.source] || "bg-white/10"}`}>{r.source}</span></td>
                          <td className="py-2.5 pr-4 text-[#9CA3AF]">{r.changed_by}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
